import React, { useMemo, useState, useEffect } from 'react';
import { Drawer, Input, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import JsonView from '../../view/JsonView.component';
import ApiErrorAlert from '../../common/ApiErrorAlert';

const RecordDetailsModal = ({ visible, onClose, record, loading = false, title, error }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  useEffect(() => {
    if (visible) setSearch('');
  }, [visible]);

  const isJsonString = (value) => {
    if (typeof value !== 'string') return false;
    try {
      JSON.parse(value);
      return value.trim().startsWith('{') || value.trim().startsWith('[');
    } catch {
      return false;
    }
  };

  const isJsonProperty = (key) => {
    const lowerKey = key.toLowerCase();
    return lowerKey.includes('json') || lowerKey === 'props' || lowerKey === 'properties';
  };

  const filterJsonProps = (obj) => {
    if (!obj || typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) {
      return obj.map(item => filterJsonProps(item));
    }
    const filtered = {};
    Object.entries(obj).forEach(([key, value]) => {
      if (isJsonProperty(key)) return;
      if (isJsonString(value)) return;
      if (typeof value === 'object' && value !== null) {
        filtered[key] = filterJsonProps(value);
      } else {
        filtered[key] = value;
      }
    });
    return filtered;
  };

  const filteredRecord = useMemo(() => {
    if (!record) return null;
    return filterJsonProps(record);
  }, [record]);

  const showContent = !loading && record != null;

  return (
    <Drawer
      title={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingRight: 24 }}>
          <span>{title || t('Record Details')}</span>
          <Input
            placeholder={t('Filter properties...')}
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            size="small"
            style={{ width: '100%' }}
          />
        </div>
      }
      placement="right"
      open={visible}
      onClose={onClose}
      width={960}
      destroyOnClose
      styles={{
        body: {
          overflowY: 'auto',
          paddingBottom: 24,
        },
      }}
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <Spin size="large" tip={t('Loading details...')} />
        </div>
      ) : error ? (
        <ApiErrorAlert error={error} />
      ) : showContent ? (
        <JsonView data={filteredRecord} search={search} onSearchChange={setSearch} />
      ) : visible ? (
        <div style={{ padding: 24, color: 'rgba(0,0,0,0.45)' }}>{t('No details available')}</div>
      ) : null}
    </Drawer>
  );
};

export default RecordDetailsModal;
